import { Drums } from '@ezchops/drums';
import { SheetEvaluator } from '@ezchops/game/lib/practice';
import { colors, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { usePracticeContext } from '../lesson/context/PracticeContext';
import { Actions } from '../lesson/elements/ControlsBar/Actions';
import { MidiState } from '../lesson/elements/ControlsBar/MidiState';
import { useScheduler } from '../lesson/hooks';
import { DrumKitContext } from './DrumKitContext';
import { Cymbal, Drum, DrumStick, HiHat, OpenHiHat, Pedal, RideBell } from './Elements';
export function Controllers() {
    const activePads = useDrumKitBot();
    const context = useMemo(() => ({
        className: (note) => activePads[note]
    }), [activePads]);
    return React.createElement(Stack, { className: "Controllers" },
        React.createElement(MidiState, null),
        React.createElement(DrumKitSVGElement, { preserveAspectRatio: "xMidYMid meet", viewBox: "-400 50 800 500" },
            React.createElement(DrumKitContext.Provider, { value: context },
                React.createElement(Drum, { id: 'snare', namePosition: "bottom", note: Drums.SNARE, cx: 0, cy: 416.5, r: 57.5 }),
                React.createElement(Drum, { id: 'tom1', namePosition: "top", note: Drums.HIGH_TOM, cx: -65, cy: 265.5, r: 57.5 }),
                React.createElement(Drum, { id: 'tom2', namePosition: "top", note: Drums.MID_TOM, cx: 61, cy: 265.5, r: 57.5 }),
                React.createElement(Drum, { id: 'tom4', namePosition: "right", note: Drums.LOW_TOM, cx: 142, cy: 364.5, r: 57.5 }),
                React.createElement(HiHat, { id: 'hihat', note: Drums.CLOSED_HI_HAT, cx: -146, cy: 364.5, r: 57.5 }),
                React.createElement(OpenHiHat, { id: 'hihat-open', note: Drums.OPEN_HI_HAT, cx: -146, cy: 364.5, r: 57.5 }),
                React.createElement(Cymbal, { id: 'crash', namePosition: "left", note: Drums.CRASH, cx: -220, cy: 205, r: 90 }),
                React.createElement(Cymbal, { id: 'ride', namePosition: "right", note: Drums.RIDE, cx: 220, cy: 205, r: 90 }),
                React.createElement(RideBell, { id: 'ride_bell', note: Drums.RIDE_BELL, cx: 224, cy: 205, r: 37 }),
                React.createElement(Pedal, { id: 'hihat_pedal', namePosition: "left", note: Drums.PEDAL_HI_HAT, x: -106, y: 444, width: 30, height: 60, r: 15 }),
                React.createElement(Pedal, { id: 'kick_pedal', namePosition: "right", note: Drums.KICK, x: 76, y: 444, width: 30, height: 60, r: 15 }),
                React.createElement(DrumStick, { sticking: "L", tip: { x: -250, y: 365 } }),
                React.createElement(DrumStick, { sticking: "R", tip: { x: 250, y: 365 } }))),
        React.createElement(Actions, null));
}
const DrumKitSVGElement = styled('svg')(({ theme }) => {
    const padStyle = {
        '&:click': { cursor: 'pointer' },
        fill: colors.grey[600],
        '&.active': {
            fill: colors.blueGrey[100]
        },
        '&.L': {
            fill: theme.palette.sticking.left.main
        },
        '&.R': {
            fill: theme.palette.sticking.right.main
        },
    };
    return {
        flexGrow: 1,
        display: 'block',
        width: '100%',
        '& g.pad': {
            '& circle': padStyle,
            '& rect': padStyle,
            '& path': padStyle,
        },
        '& g.pedal': {
            '& rect': {
                ...padStyle,
            },
        },
        '& .drumName': { display: 'none' },
        '& .ride_bell': {
            '& .drumName': {
                color: colors.common.white,
            }
        },
        '& .stick': padStyle
    };
});
function useDrumKitBot() {
    const { clock, clockState, evaluator } = usePracticeContext();
    const { timing } = evaluator;
    const [active, setActive] = useState({
        [Drums.PEDAL_HI_HAT]: 'L'
    });
    const onTick = useCallback((tick) => {
        tick.notes.forEach((note, i) => {
            const matchingHit = note.pitch == null ? evaluator.evaluation[tick.absTick].evaluated.find((e) => e.expected == note)?.hit : undefined;
            const pitch = note.pitch ?? matchingHit?.note;
            if (pitch != null) {
                const timestamp = tick.timestamp;
                setTimeout(() => {
                    setActive((active) => ({ ...active, [pitch]: note.sticking ?? 'active' }));
                    setTimeout(() => {
                        setActive((active) => ({ ...active, [pitch]: undefined }));
                    }, 100);
                }, (timestamp - clock.now) * 1000);
            }
        });
    }, [clock, evaluator]);
    const cursor = useRef(0);
    const reset = useCallback(() => {
        cursor.current = 0;
    }, []);
    const seek = useCallback((timestamp) => {
        cursor.current = Math.max(0, evaluator.ticks.findIndex(tick => tick.timestamp > timestamp));
    }, [evaluator]);
    const ticks = useMemo(() => evaluator instanceof SheetEvaluator ? evaluator.ticks : [], [evaluator]);
    useScheduler(timing, clock, clockState, ticks, onTick, reset, seek, cursor);
    return active;
}
//# sourceMappingURL=Controllers.js.map